import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  CogIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  WifiIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../../config/api';

import { 
  Card, 
  CardContent, 
  CardHeader,
  Button,
  Input,
  Label,
  Textarea,
  Switch,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Alert
} from '../../../../components/ui';

interface ServicePlan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  business_type: string;
  monthly_price: number;
  features: {
    max_seats: number;
    max_locations: number;
    api_calls_per_month: number;
    priority_support: boolean;
    custom_features: boolean;
    analytics_level: string;
    backup_retention_days: number;
    sms_credits: number;
    sms_unit_price: number;
    [key: string]: any;
  };
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PlanStatistics {
  total_plans: number;
  active_plans: number;
  inactive_plans: number;
  total_monthly_revenue: number;
  plans_summary: Array<{
    plan_id: string;
    plan_name: string;
    monthly_price: number;
    subscriber_count: number;
    monthly_revenue: number;
    max_seats: number;
  }>;
}

const ServicePlanManagement: React.FC = () => {
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [statistics, setStatistics] = useState<PlanStatistics | null>(null);
  const [editingPlan, setEditingPlan] = useState<ServicePlan | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('studyroom');
  const [showBusinessTypeForm, setShowBusinessTypeForm] = useState(false);

  // 비즈니스 타입 정의
  const businessTypes = [
    { value: 'studyroom', label: '독서실', icon: BuildingOfficeIcon },
    { value: 'academy', label: '학원', icon: BuildingOfficeIcon },
    { value: 'library', label: '도서관', icon: BuildingOfficeIcon },
    { value: 'coworking', label: '코워킹스페이스', icon: BuildingOfficeIcon },
    { value: 'cafe', label: '스터디카페', icon: BuildingOfficeIcon },
  ];

  // 새 플랜 생성용 상태
  const [newPlan, setNewPlan] = useState({
    name: '',
    display_name: '',
    description: '',
    business_type: 'studyroom',
    monthly_price: 0,
    max_seats: 20,
    max_locations: 1,
    api_calls_per_month: 50000,
    priority_support: false,
    custom_features: false,
    analytics_level: 'basic',
    backup_retention_days: 7,
    sms_credits: 100,
    sms_unit_price: 30
  });

  useEffect(() => {
    loadPlans();
    loadStatistics();
  }, [selectedBusinessType]);

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`/v1/admin/service-plans?business_type=${selectedBusinessType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`플랜 로드 실패: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // 비즈니스 타입이 없는 기존 플랜들에 기본값 할당
      const plansWithBusinessType = data.map((plan: any) => ({
        ...plan,
        business_type: plan.business_type || 'studyroom'
      }));
      setPlans(plansWithBusinessType);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load plans:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
      } else {
        setError(err instanceof Error ? err.message : '플랜 로드 중 오류 발생');
      }
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch(`/v1/admin/service-plans-statistics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      console.error('통계 로드 실패:', err);
    }
  };

  const createPlan = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/v1/admin/service-plans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPlan),
      });

      if (!response.ok) {
        throw new Error('플랜 생성 실패');
      }

      setSuccess('플랜이 성공적으로 생성되었습니다.');
      setShowCreateForm(false);
      setNewPlan({
        name: '',
        display_name: '',
        description: '',
        business_type: 'studyroom',
        monthly_price: 0,
        max_seats: 20,
        max_locations: 1,
        api_calls_per_month: 50000,
        priority_support: false,
        custom_features: false,
        analytics_level: 'basic',
        backup_retention_days: 7,
        sms_credits: 100,
        sms_unit_price: 30
      });
      await loadPlans();
      await loadStatistics();
    } catch (err) {
      setError(err instanceof Error ? err.message : '플랜 생성 중 오류 발생');
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = async (plan: ServicePlan) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/v1/admin/service-plans/${plan.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: plan.display_name,
          description: plan.description,
          monthly_price: plan.monthly_price,
          max_seats: plan.features.max_seats,
          max_locations: plan.features.max_locations,
          api_calls_per_month: plan.features.api_calls_per_month,
          priority_support: plan.features.priority_support,
          custom_features: plan.features.custom_features,
          analytics_level: plan.features.analytics_level,
          backup_retention_days: plan.features.backup_retention_days,
          sms_credits: plan.features.sms_credits,
          sms_unit_price: plan.features.sms_unit_price,
          is_active: plan.is_active,
          sort_order: plan.sort_order
        }),
      });

      if (!response.ok) {
        throw new Error('플랜 수정 실패');
      }

      setSuccess('플랜이 성공적으로 수정되었습니다.');
      setEditingPlan(null);
      await loadPlans();
      await loadStatistics();
    } catch (err) {
      setError(err instanceof Error ? err.message : '플랜 수정 중 오류 발생');
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (planId: string) => {
    if (!window.confirm('정말로 이 플랜을 삭제하시겠습니까?')) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/v1/admin/service-plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('플랜 삭제 실패');
      }

      setSuccess('플랜이 성공적으로 삭제되었습니다.');
      await loadPlans();
      await loadStatistics();
    } catch (err) {
      setError(err instanceof Error ? err.message : '플랜 삭제 중 오류 발생');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR', { 
      style: 'currency', 
      currency: 'KRW' 
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">서비스 플랜 관리</h1>
          <p className="text-gray-600 mt-2">비즈니스 타입별 서비스 플랜을 관리합니다</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowBusinessTypeForm(true)}
            className="flex items-center gap-2"
          >
            <CogIcon className="h-4 w-4" />
            비즈니스 타입 관리
          </Button>
          <Button 
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            새 플랜 생성
          </Button>
        </div>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
          <div className="text-red-700">{error}</div>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckIcon className="h-4 w-4 text-green-600" />
          <div className="text-green-700">{success}</div>
        </Alert>
      )}

      {/* 비즈니스 타입 선택 */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">비즈니스 타입:</span>
          <div className="flex space-x-2">
            {businessTypes.map((type) => (
              <Button
                key={type.value}
                variant={selectedBusinessType === type.value ? "primary" : "outline"}
                size="sm"
                onClick={() => setSelectedBusinessType(type.value)}
                className="flex items-center gap-2"
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="plans" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="plans">플랜 관리</TabsTrigger>
          <TabsTrigger value="statistics">통계 및 분석</TabsTrigger>
        </TabsList>

        {/* 플랜 관리 탭 */}
        <TabsContent value="plans" className="space-y-4">
          {/* 통계 카드 */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">전체 플랜</p>
                      <p className="text-2xl font-bold">{statistics.total_plans}</p>
                    </div>
                    <CogIcon className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">활성 플랜</p>
                      <p className="text-2xl font-bold text-green-600">{statistics.active_plans}</p>
                    </div>
                    <CheckIcon className="h-8 w-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">월 매출</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatPrice(statistics.total_monthly_revenue)}
                      </p>
                    </div>
                    <CurrencyDollarIcon className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">구독자 수</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {statistics.plans_summary.reduce((total, plan) => total + plan.subscriber_count, 0)}
                      </p>
                    </div>
                    <UsersIcon className="h-8 w-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* 플랜 생성 폼 */}
          {showCreateForm && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold flex items-center justify-between">
                  새 서비스 플랜 생성
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </Button>
                </h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="business_type">비즈니스 타입</Label>
                    <select
                      id="business_type"
                      value={newPlan.business_type}
                      onChange={(e) => setNewPlan({...newPlan, business_type: e.target.value})}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="name">플랜 ID</Label>
                    <Input
                      id="name"
                      value={newPlan.name}
                      onChange={(e) => setNewPlan({...newPlan, name: e.target.value})}
                      placeholder="starter, standard, pro..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="display_name">플랜 이름</Label>
                    <Input
                      id="display_name"
                      value={newPlan.display_name}
                      onChange={(e) => setNewPlan({...newPlan, display_name: e.target.value})}
                      placeholder="스타터 독서실"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">설명</Label>
                  <Textarea
                    id="description"
                    value={newPlan.description}
                    onChange={(e) => setNewPlan({...newPlan, description: e.target.value})}
                    placeholder="플랜에 대한 설명을 입력하세요"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="monthly_price">월 가격 (원)</Label>
                    <Input
                      id="monthly_price"
                      type="number"
                      value={newPlan.monthly_price}
                      onChange={(e) => setNewPlan({...newPlan, monthly_price: Number(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_seats">최대 좌석 수</Label>
                    <Input
                      id="max_seats"
                      type="number"
                      value={newPlan.max_seats}
                      onChange={(e) => setNewPlan({...newPlan, max_seats: Number(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_locations">최대 지점 수</Label>
                    <Input
                      id="max_locations"
                      type="number"
                      value={newPlan.max_locations}
                      onChange={(e) => setNewPlan({...newPlan, max_locations: Number(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sms_credits">월 SMS 크레딧</Label>
                    <Input
                      id="sms_credits"
                      type="number"
                      value={newPlan.sms_credits}
                      onChange={(e) => setNewPlan({...newPlan, sms_credits: Number(e.target.value)})}
                      placeholder="무료 제공 SMS 수"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sms_unit_price">SMS 건당 단가 (원)</Label>
                    <Input
                      id="sms_unit_price"
                      type="number"
                      value={newPlan.sms_unit_price}
                      onChange={(e) => setNewPlan({...newPlan, sms_unit_price: Number(e.target.value)})}
                      placeholder="초과시 건당 요금"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    취소
                  </Button>
                  <Button
                    onClick={createPlan}
                    disabled={saving}
                    className="flex items-center gap-2"
                  >
                    {saving ? '생성 중...' : '플랜 생성'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 플랜 목록 */}
          <div className="grid gap-4">
            {plans.map((plan) => (
              <Card key={plan.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">{plan.display_name}</h3>
                      <Badge variant={plan.is_active ? "default" : "secondary"}>
                        {plan.is_active ? "활성" : "비활성"}
                      </Badge>
                      <Badge variant="outline">
                        {formatPrice(plan.monthly_price)}/월
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingPlan(plan)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => deletePlan(plan.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">최대 {plan.features.max_seats}석</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <UsersIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">지점 {plan.features.max_locations}개</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <WifiIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">API {plan.features.api_calls_per_month.toLocaleString()}회/월</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">분석: {plan.features.analytics_level}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">SMS {plan.features.sms_credits}건/월</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">초과시 {plan.features.sms_unit_price}원/건</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {plan.features.priority_support && (
                      <Badge variant="secondary" className="text-xs">우선지원</Badge>
                    )}
                    {plan.features.custom_features && (
                      <Badge variant="secondary" className="text-xs">커스텀기능</Badge>
                    )}
                    {plan.features.advanced_analytics && (
                      <Badge variant="secondary" className="text-xs">고급분석</Badge>
                    )}
                    {plan.features.webhook_support && (
                      <Badge variant="secondary" className="text-xs">웹훅</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* 편집 모달 */}
          {editingPlan && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setEditingPlan(null)}
            >
              <div
                className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
              <Card className="w-full">
                <CardHeader>
                  <h3 className="text-lg font-semibold flex items-center justify-between">
                    플랜 편집: {editingPlan.display_name}
                    <Button
                      variant="outline"
                      onClick={() => setEditingPlan(null)}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  </h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>비즈니스 타입</Label>
                      <select
                        value={editingPlan.business_type || 'studyroom'}
                        onChange={(e) => setEditingPlan({...editingPlan, business_type: e.target.value})}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {businessTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>플랜 이름</Label>
                      <Input
                        value={editingPlan.display_name}
                        onChange={(e) => setEditingPlan({...editingPlan, display_name: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>설명</Label>
                    <Textarea
                      value={editingPlan.description}
                      onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>월 가격 (원)</Label>
                      <Input
                        type="number"
                        value={editingPlan.monthly_price}
                        onChange={(e) => setEditingPlan({...editingPlan, monthly_price: Number(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>최대 좌석 수</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.max_seats}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, max_seats: Number(e.target.value)}
                        })}
                      />
                    </div>
                    <div>
                      <Label>최대 지점 수</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.max_locations}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, max_locations: Number(e.target.value)}
                        })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>월 SMS 크레딧</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.sms_credits}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, sms_credits: Number(e.target.value)}
                        })}
                      />
                    </div>
                    <div>
                      <Label>SMS 건당 단가 (원)</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.sms_unit_price}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, sms_unit_price: Number(e.target.value)}
                        })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>API 호출 한도 (월)</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.api_calls_per_month}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, api_calls_per_month: Number(e.target.value)}
                        })}
                      />
                    </div>
                    <div>
                      <Label>백업 보관 일수</Label>
                      <Input
                        type="number"
                        value={editingPlan.features.backup_retention_days}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, backup_retention_days: Number(e.target.value)}
                        })}
                      />
                    </div>
                    <div>
                      <Label>분석 레벨</Label>
                      <select
                        value={editingPlan.features.analytics_level}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, analytics_level: e.target.value}
                        })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="basic">기본</option>
                        <option value="advanced">고급</option>
                        <option value="premium">프리미엄</option>
                        <option value="enterprise">엔터프라이즈</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingPlan.features.priority_support}
                        onCheckedChange={(checked) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, priority_support: checked}
                        })}
                      />
                      <Label>우선 지원</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingPlan.features.custom_features}
                        onCheckedChange={(checked) => setEditingPlan({
                          ...editingPlan, 
                          features: {...editingPlan.features, custom_features: checked}
                        })}
                      />
                      <Label>커스텀 기능</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingPlan.is_active}
                        onCheckedChange={(checked) => setEditingPlan({
                          ...editingPlan, 
                          is_active: checked
                        })}
                      />
                      <Label>플랜 활성화</Label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setEditingPlan(null)}
                    >
                      취소
                    </Button>
                    <Button
                      onClick={() => updatePlan(editingPlan)}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      {saving ? '저장 중...' : '변경사항 저장'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* 통계 탭 */}
        <TabsContent value="statistics" className="space-y-4">
          {statistics && (
            <div className="grid gap-4">
              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold">플랜별 구독 현황</h3>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {statistics.plans_summary.map((summary) => (
                      <div key={summary.plan_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">{summary.plan_name}</h4>
                          <p className="text-sm text-gray-600">
                            구독자 {summary.subscriber_count}명 · 최대 {summary.max_seats}석
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatPrice(summary.monthly_revenue)}/월</p>
                          <p className="text-sm text-gray-600">
                            {formatPrice(summary.monthly_price)} × {summary.subscriber_count}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ServicePlanManagement;